import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Story, User } from '../../models/story.model';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-story-viewer',
  templateUrl: './story-viewer.component.html',
  styleUrls: ['./story-viewer.component.css']
})
export class StoryViewerComponent implements OnInit, OnDestroy {
  @ViewChild('videoPlayer') videoPlayer: ElementRef<HTMLVideoElement>;
  stories: Story[] = [];
  currentUser: User | null = null;
  currentIndex: number = 0;
  progress: number = 0;
  storyDuration: number = 5000; // 5 secondes par défaut
  private timer: any;
  private isVideoPlaying: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<StoryViewerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { stories: Story[], currentUser: User },
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    this.stories = data.stories || [];
    this.currentUser = data.currentUser;
  }

  ngOnInit() {
    this.startProgress();
  }

  ngOnDestroy() {
    this.clearTimer();
  }

  onVideoLoaded(event: Event) {
    const video = event.target as HTMLVideoElement;
    if (video) {
      console.log('Vidéo chargée, durée:', video.duration);
      this.storyDuration = video.duration * 1000;
      video.play().catch(error => {
        console.error('Erreur lors de la lecture de la vidéo:', error);
      });
      this.isVideoPlaying = true;
    }
  }

  onVideoError(event: Event) {
    console.error('Erreur de chargement de la vidéo:', event);
    const video = event.target as HTMLVideoElement;
    if (video) {
      console.error('Code d\'erreur:', video.error);
    }
  }

  onVideoTimeUpdate(event: Event) {
    if (!this.isVideoPlaying) return;
    
    const video = event.target as HTMLVideoElement;
    if (video) {
      this.progress = (video.currentTime / video.duration) * 100;
      this.cdr.detectChanges();
    }
  }

  startProgress() {
    this.clearTimer();
    this.progress = 0;
    this.isVideoPlaying = false;
    
    const currentStory = this.stories[this.currentIndex];
    
    if (currentStory?.type === 'video') {
      // Pour les vidéos, la progression est gérée par les événements de la vidéo
      if (this.videoPlayer?.nativeElement) {
        this.videoPlayer.nativeElement.currentTime = 0;
        this.videoPlayer.nativeElement.play().catch(error => {
          console.error('Erreur lors de la lecture de la vidéo:', error);
        });
      }
    } else {
      // Pour les images, utiliser le timer
      this.storyDuration = 5000;
      const startTime = Date.now();
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        this.progress = (elapsed / this.storyDuration) * 100;
        
        if (this.progress >= 100) {
          this.nextStory();
        } else {
          this.timer = requestAnimationFrame(updateProgress);
        }
        this.cdr.detectChanges();
      };
      this.timer = requestAnimationFrame(updateProgress);
    }
  }

  clearTimer() {
    if (this.timer) {
      cancelAnimationFrame(this.timer);
      this.timer = null;
    }
    if (this.videoPlayer?.nativeElement) {
      this.videoPlayer.nativeElement.pause();
      this.isVideoPlaying = false;
    }
  }

  nextStory() {
    this.clearTimer();
    if (this.currentIndex < this.stories.length - 1) {
      this.currentIndex++;
      setTimeout(() => this.startProgress(), 0);
    } else {
      this.close();
    }
  }

  previousStory() {
    this.clearTimer();
    if (this.currentIndex > 0) {
      this.currentIndex--;
      setTimeout(() => this.startProgress(), 0);
    }
  }

  close() {
    this.clearTimer();
    this.dialogRef.close();
  }

  formatTimestamp(timestamp: any): string {
    if (!timestamp) return '';
    
    const date = typeof timestamp === 'number' 
      ? new Date(timestamp)
      : new Date(timestamp.seconds * 1000);
    
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}j`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return 'À l\'instant';
  }

  getCurrentStory(): Story | null {
    return this.stories[this.currentIndex] || null;
  }
}
